import React, { ReactNode, createContext, useContext, useState, useEffect } from 'react';

import type { BuiltFonts, FontConfig } from 'utils/font';

const FontOverrideContext = createContext(null);

export const FontOverrideProvider = ({
  fonts: initialFonts,
  fontLoadQueryString: initialFontLoadQueryString,
  pagePath,
  children,
}: {
  fonts: FontConfig;
  fontLoadQueryString: string;
  pagePath?: string;
  children: ReactNode;
}) => {
  const [fontData, setFontData] = useState({
    fonts: initialFonts,
    fontLoadQueryString: initialFontLoadQueryString,
  });
  const [currentPath, setCurrentPath] = useState(pagePath);

  // Reset state for new path to avoid upsell pages maintaining initial funnel data from the first upsell.
  // This may not occur before the first render of other components; they may see
  // the previous page's pageData for initial render before this updates and causes
  // another render.
  useEffect(() => {
    if (pagePath !== currentPath) {
      setFontData({ fonts: initialFonts, fontLoadQueryString: initialFontLoadQueryString });
      setCurrentPath(pagePath);
    }
  }, [currentPath, pagePath, initialFonts, initialFontLoadQueryString]);

  return (
    <FontOverrideContext.Provider value={{ ...fontData }}>{children}</FontOverrideContext.Provider>
  );
};

export function useFontOverride(): {
  fonts: BuiltFonts;
  fontLoadQueryString: string;
} {
  const context = useContext(FontOverrideContext);
  if (context === undefined) {
    throw new Error('useFontOverride must be used within a FontOverrideProvider');
  }

  return context;
}
